import { RefObject, useRef, useState } from "react";

import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { BillingPaymentMethodsHelper } from "@/Common/Helpers/BillingPaymentMethodsHelper";

import styles from "./MarkBillAsPaidPopup.module.scss";

interface MarkBillAsPaidPopupProps {
    onClose: () => void;
    onSubmit: (paymentMethod: string, paymentDate: Date) => void;
}

function MarkBillAsPaidPopup(props: MarkBillAsPaidPopupProps): JSX.Element {
    const paymentMethods = BillingPaymentMethodsHelper.getPaymentMethodsDictionnaryWithoutPayableAndReceivable();
    const payementMethodDropdownRef: RefObject<SelectLookupControl> = React.createRef();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>();
    const [paymentDate, setPaymentDate] = useState(new Date());
    const formIsValid = !!selectedPaymentMethod && !!paymentDate;
    const { getText } = useTranslation();

    const [disableActionButtons, setDisableActionButtons] = useState(false);
    const disableSubmitCall = useRef(false);
    const disableButtons = async (callback: () => void): Promise<void> => {
        if (disableSubmitCall.current)
            return;

        setDisableActionButtons(true);
        disableSubmitCall.current = true;

        await callback();

        setDisableActionButtons(false);
        disableSubmitCall.current = false;
    };

    const onSubmitCall = async (): Promise<void> => {
        await props.onSubmit(selectedPaymentMethod, paymentDate);
    };

    const footer = (
        <div className="flex-container pull-right">
            <button className="btn btn-default push-right-5" onClick={props.onClose}>
                {getText("CancelLabel")}
            </button>
            <button
                className="btn btn-primary push-right-5"
                onClick={() => disableButtons(() => onSubmitCall())}
                disabled={!formIsValid || disableActionButtons}
            >
                {getText("SaveLabel")}
            </button>
        </div>
    );

    return (
        <Modal
            title={getText("CloseBillLabel")}
            onClose={props.onClose}
            width={510}
            footer={footer}
            showOverlay
            showX
        >
            <div className={styles.markAsPaidPopup} onClick={e => e.stopPropagation()}>
                <label>{getText("PaymentMethodLabel")}</label>
                <SelectLookupControl
                    invalid={!selectedPaymentMethod}
                    Value={selectedPaymentMethod}
                    AvailableValues={Object.keys(paymentMethods)
                        .map(payementMethodKey => new ComboBoxDropDownItem(payementMethodKey, paymentMethods[payementMethodKey]))
                    }
                    Callback={value => setSelectedPaymentMethod(value)}
                    ref={payementMethodDropdownRef}
                    IncludeEmptyChoice={true}
                />
                <label>{getText("PaymentDateLabel")}</label>
                <DateSelector
                    id="privateBill_create_date"
                    cssClass=""
                    dateFormat="dddd, MMM dd, yyyy"
                    disabled={false}
                    date={paymentDate}
                    onChangeCallback={value => setPaymentDate(value)}
                />
            </div>
        </Modal>
    );
}

export { MarkBillAsPaidPopup };
